// Status cards/forms share raw URL mounts and the warm-cache native renderer.

import { paintCachedStatus, paintStatusMount } from './legacy-mount';
import { renderCreatureStatusForm, ftRerenderCreatureStatusList } from './render-creature-status-form';
import { findStatusEntry } from './resolve-refs';
import { fillStatusList as fillSharedStatusList } from './status-row';
import { findInventoryInstanceNode } from '../object-card/find-cached-item';
import type { CasterBlob, StatusEntry, EditCtx } from './types';

const SELECTOR = '[data-ft-status-effect]';
const DONE_ATTR = 'data-ft-done';

type UnknownRecord = Record<string, unknown>;
type CacheMapLike = { values(): Iterable<unknown> };

function recordOf(value: unknown): UnknownRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as UnknownRecord
        : null;
}

function cacheMapOf(value: unknown): CacheMapLike | null {
    const record = recordOf(value);
    return record && typeof record.values === 'function'
        ? record as unknown as CacheMapLike
        : null;
}

function asRows(value: unknown): unknown[] {
    return Array.isArray(value) ? value : (recordOf(value) ? Object.values(value as UnknownRecord) : []);
}

function getCaster(id: string): CasterBlob | undefined {
    return id ? (window.FateTide?.casterCache?.get(id) as CasterBlob | undefined) : undefined;
}

/** Warm cache first; cold-fetch the caster blob (websocket bundle) on a miss. */
async function loadCaster(id: string): Promise<CasterBlob | undefined> {
    if (!id) return undefined;
    let c = getCaster(id);
    if (!c && typeof fetchAndCacheCaster === 'function') {
        await fetchAndCacheCaster(id);
        c = getCaster(id);
    }
    if (c) await window.ftAdoptInventoryBase?.(c);
    return c;
}

async function paint(el: HTMLElement): Promise<void> {
    const statusId = el.getAttribute('data-ft-object-id') || '';
    const casterId = el.getAttribute('data-ft-caster-id') || '';
    const targetId = el.getAttribute('data-ft-target-id') || '';
    if (!statusId) return;

    let entry = findStatusInstance(statusId, targetId || casterId);
    if (!el.querySelector('.ft-status-blob') && !entry) {
        entry = findStatusEntry(await loadCaster(targetId || casterId), statusId);
        if (!entry && casterId !== targetId) entry = findStatusEntry(await loadCaster(casterId), statusId);
    }
    if (await paintStatusMount(el, entry)) el.setAttribute(DONE_ATTR, '1');
}

const mounting = new WeakSet<HTMLElement>();
function mountOne(el: HTMLElement): void {
    if (el.getAttribute(DONE_ATTR) === '1' || mounting.has(el)) return;
    mounting.add(el);
    void paint(el).catch(error => console.error('Status surface could not load', error))
        .finally(() => mounting.delete(el));
}

/** Scan a root for un-mounted status-effect mounts and paint them. */
function ftMountStatusEffects(root: ParentNode = document): void {
    root.querySelectorAll<HTMLElement>(`${SELECTOR}:not([${DONE_ATTR}="1"])`).forEach(mountOne);
}

/**
 * Resolve a status instance by id from the WARM caster cache — owner blob first (caller's context),
 * then a scan of every cached caster (the status is full-nested on both its caster's .caster list and
 * its target's .target list, so any owner that has it cached resolves it). Synchronous; no cold fetch.
 */
function findStatusInstance(statusId: string, ownerId?: string): StatusEntry | undefined {
    if (!statusId) return undefined;
    let entry = ownerId ? findStatusEntry(getCaster(ownerId), statusId) : undefined;
    if (entry) return entry;

    const ft = window.FateTide;

    // 1) casterCache — a status carried on its caster's/target's caster blob (statuses.caster/target).
    const cc = cacheMapOf(ft?.casterCache);
    if (cc) {
        for (const root of cc.values()) {
            entry = findStatusEntry(root as CasterBlob | undefined, statusId);
            if (entry) return entry;
        }
    }

    // 2) encounterCache — persistent-AOE statuses live on the ENCOUNTER blob, NOT a caster blob: either the
    //    GLOBAL list (encounterArr.statuses) or attached under a creature/inventory row's own statuses.
    //    findStatusEntry reads `.statuses.caster/target` off whatever blob it's given, so the encounter blob
    //    and each member row both work. This is what lets a double-clicked map pAOE (e.g. a Darkness Sphere
    //    following a creature) resolve from cache instead of falling through to the AJAX form.
    const ec = cacheMapOf(ft?.encounterCache);
    if (ec) {
        for (const enc of ec.values()) {
            entry = findStatusEntry(enc as CasterBlob | undefined, statusId);
            if (entry) return entry;
            const encounter = recordOf(enc);
            if (!encounter) continue;
            for (const listKey of ['encounter_creatures_arr', 'encounter_inventories_arr']) {
                for (const rowValue of asRows(encounter[listKey])) {
                    const row = recordOf(rowValue);
                    if (!row) continue;
                    entry = findStatusEntry(row as CasterBlob | undefined, statusId);
                    if (entry) return entry;
                    // A creature carries its own inventory rows under `.inventory[]`, and a persistent-AOE
                    // status can be anchored to a CARRIED item (statuses nest on that inventory row, not on
                    // the top-level encounter_inventories_arr) — descend one level so those resolve from
                    // cache too instead of falling through to the AJAX form.
                    for (const inv of asRows(row.inventory)) {
                        entry = findStatusEntry(inv as CasterBlob | undefined, statusId);
                        if (entry) return entry;
                    }
                }
            }
        }
    }

    return undefined;
}

/** Recognize cached status identities, then mount the saved context through the shared native card. */
function ftRenderStatusInstanceFromCache(statusId: string, container: HTMLElement, ownerId?: string): boolean {
    if (!statusId || !container) return false;
    const entry = findStatusInstance(statusId, ownerId);
    if (!entry) return false;
    void paintCachedStatus(statusId, container, ownerId)
        .catch(error => console.error('Status card could not load', error));
    return true;
}

// ── INVENTORY STATUS LIST (shared renderer) ──────────────────────────────────────────────────────
// The inventory "Modify Status Effects" form (form_modify_inventory_status_effects_inc.php) emits a
// mount `<div data-ft-inventory-status-list="<inventory_id>" data-campaign-id data-encounter-id
// data-can-edit data-can-campaign>` whose initial children are the SERVER-rendered fallback list. These
// functions replace that fallback with the SHARED client rows (status-row.ts) — the exact rows the
// creature status form draws — read from the warm cache (statuses.target on the inventory node). On a
// cache miss the server fallback is left untouched (never an empty list).

function invStatusCtx(mount: HTMLElement, inventoryId: string): EditCtx {
    return {
        statusId: '',
        campaignId: mount.getAttribute('data-campaign-id') || '',
        encounterId: mount.getAttribute('data-encounter-id') || '',
        casterId: inventoryId,
    };
}

/** Permissions are SERVER-authoritative (stamped on the mount); the client only honors them visually. */
function invStatusPerms(mount: HTMLElement): { canEdit: boolean; canCampaign: boolean } {
    return {
        canEdit: mount.getAttribute('data-can-edit') === '1',
        canCampaign: mount.getAttribute('data-can-campaign') === '1',
    };
}

/** Paint an inventory's `statuses.target` into the mount via the shared rows. Returns false (leaving the
 *  server fallback in place) on a cache miss or bad input. */
function ftRenderInventoryStatusList(inventoryId: string, mount: HTMLElement): boolean {
    if (!inventoryId || !mount) return false;
    const node = findInventoryInstanceNode(inventoryId);
    if (!node) return false; // keep the server fallback; a later scan re-tries once the cache fills
    const statusBuckets = recordOf(node.statuses);
    const statuses = asRows(statusBuckets?.target)
        .map(recordOf)
        .filter((entry): entry is UnknownRecord => entry !== null) as StatusEntry[];
    fillSharedStatusList(mount, statuses, invStatusCtx(mount, inventoryId), invStatusPerms(mount));
    return true;
}

/** Scan a root for un-painted inventory status mounts and enhance them from cache (idempotent). */
function ftMountInventoryStatusLists(root: ParentNode = document): void {
    window.ftMountStatusEffects?.(root);
    root.querySelectorAll<HTMLElement>('[data-ft-inventory-status-list]:not([data-ft-done="1"])').forEach((el) => {
        const invId = el.getAttribute('data-ft-inventory-status-list') || '';
        if (ftRenderInventoryStatusList(invId, el)) el.setAttribute('data-ft-done', '1');
        else void loadCaster(invId).then(() => {
            if (el.isConnected && ftRenderInventoryStatusList(invId, el)) el.setAttribute(DONE_ATTR, '1');
        }).catch(error => console.error('Inventory status list could not load', error));
    });
    // loadPostFunctions runs this after every AJAX inject; show_creature_statuses_inc.php's creature list is mounted here too.
    root.querySelectorAll<HTMLElement>('[data-ft-creature-status-list]:not([data-ft-done="1"])').forEach(el => {
        const id = el.getAttribute('data-ft-creature-status-list') || '';
        void loadCaster(id).then(caster => {
            if (!caster || !el.isConnected) return;
            ftRerenderCreatureStatusList(id);
            el.setAttribute(DONE_ATTR, '1');
        }).catch(error => console.error('Creature status list could not load', error));
    });
}

/** Repaint an OPEN inventory status list off the freshly cached node (WS caster_update side effect),
 *  the twin of ftRerenderCreatureStatusList. No-op when no such list is open for this inventory. */
function ftRerenderInventoryStatusList(inventoryId: string): void {
    if (!inventoryId) return;
    const safe = String(inventoryId).replace(/["\\]/g, '');
    const el = document.querySelector<HTMLElement>(`[data-ft-inventory-status-list="${safe}"]`);
    if (!el) return;
    ftRenderInventoryStatusList(inventoryId, el);
}

Object.assign(window, {
    ftFindStatusInstance: findStatusInstance,
    ftMountStatusEffects,
    renderCreatureStatusForm,
    ftRerenderCreatureStatusList,
    ftRenderStatusInstanceFromCache,
    ftRenderInventoryStatusList,
    ftMountInventoryStatusLists,
    ftRerenderInventoryStatusList,
});

// Page-load mount once the DOM is ready (no-op on pages with no mount). AJAX surfaces call
// window.ftMountStatusEffects() explicitly after injection.
(function ftInitStatusEffect(): void {
    ftMountInventoryStatusLists();
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => ftMountInventoryStatusLists());
    }
})();
